import {
  type BalancePaymentAppliedHistoryData,
  type BalanceRefundAppliedHistoryData,
  EstimateHistoryModel,
  LOG_TYPE,
} from '@mmx/shared'

export interface DescribeHistoryEntryDeps {
  formatMoney: (amount: number) => string
  actorName: string
  typeName: string
  t: (key: string, params?: Record<string, string | number>) => string
}

const NS = 'balance.history.entry'

export function describeEstimateHistoryEntry(
  log: EstimateHistoryModel,
  deps: DescribeHistoryEntryDeps,
): string {
  const actor = deps.actorName
  const type = log.type as LOG_TYPE
  const data = log.messageData ?? {}

  switch (type) {
    case LOG_TYPE.ESTIMATE_BALANCE_FINALIZED:
      return data['refinalize'] === true
        ? deps.t(`${NS}.confirmed-refinalized`, { actor })
        : deps.t(`${NS}.confirmed`, { actor })
    case LOG_TYPE.ESTIMATE_MODIFY_STARTED:
      return deps.t(`${NS}.modify-started`, { actor })
    case LOG_TYPE.ESTIMATE_MODIFY_DISCARDED:
      return deps.t(`${NS}.modify-discarded`, { actor })
    case LOG_TYPE.ESTIMATE_BALANCE_REVERTED:
      return deps.t(`${NS}.reverted`, { actor })
    case LOG_TYPE.ESTIMATE_BALANCE_PAYMENT_APPLIED: {
      // {{actor}} surfaces the clinician name (resolved by the caller via
      // log.actorName). The Stripe/transaction id stays on messageData.source
      // for audit/cross-reference but is intentionally not in the human label
      // — those values are UUIDs or opaque provider strings that read poorly.
      const payment = readBalancePayment(data)
      return deps.t(`${NS}.payment-applied`, {
        actor,
        amount: deps.formatMoney(payment.amount),
      })
    }
    case LOG_TYPE.ESTIMATE_BALANCE_REFUND_APPLIED: {
      const refund = readBalanceRefund(data)
      return deps.t(`${NS}.refund-applied`, {
        actor,
        amount: deps.formatMoney(refund.amount),
      })
    }
    case LOG_TYPE.ESTIMATE_BALANCE_RECALCULATED:
      return deps.t(`${NS}.recalculated`, { actor })
    case LOG_TYPE.ESTIMATE_BALANCE_CREATED:
      return deps.t(`${NS}.balance-created`, { actor })
    case LOG_TYPE.ESTIMATE_BALANCE_MARKED_NA:
      return deps.t(`${NS}.marked-na`, { actor })
    case LOG_TYPE.ESTIMATE_BALANCE_MARKED_NOT_READY:
      return deps.t(`${NS}.marked-not-ready`, { actor })
    case LOG_TYPE.ESTIMATE_BALANCE_MARKED_NOT_STARTED:
      return deps.t(`${NS}.marked-not-started`, { actor })
    case LOG_TYPE.ESTIMATE_BALANCE_PAYMENT_PLAN_CREATED: {
      const months = readNumber(data, 'months')
      const monthlyAmount = readNumber(data, 'monthlyAmount')
      const firstPayment = readNumber(data, 'firstPayment')
      // Stripe schedules `months - 1` installments after the upfront firstPayment
      // (see balance-payment-plan.service.ts). Surface the schedule count, not
      // the total — clinicians read this and add the upfront mentally.
      const installments = Math.max(0, months - 1)
      return deps.t(`${NS}.payment-plan-created`, {
        actor,
        installments,
        monthlyAmount: deps.formatMoney(monthlyAmount),
        firstPayment: deps.formatMoney(firstPayment),
      })
    }
    case LOG_TYPE.ESTIMATE_BALANCE_PAYMENT_PLAN_CANCELED:
      return deps.t(`${NS}.payment-plan-canceled`, { actor })
    case LOG_TYPE.ESTIMATE_INVOICE_SENT:
      return deps.t(`${NS}.sent`, { actor, method: readString(data, 'method') ?? '' })
    case LOG_TYPE.ESTIMATE_FAILED_INVOICE_SENT:
      return deps.t(`${NS}.send-failed`, { actor, method: readString(data, 'method') ?? '' })
    default:
      return deps.t(`${NS}.fallback`, { actor, type: deps.typeName })
  }
}

function readNumber(data: Record<string, unknown>, key: string): number {
  const v = data[key]
  return typeof v === 'number' ? v : 0
}

function readString(data: Record<string, unknown>, key: string): string | undefined {
  const v = data[key]
  return typeof v === 'string' ? v : undefined
}

// Defensive readers for the shared per-event payloads. The recorder writes
// these as typed BalancePaymentAppliedHistoryData / BalanceRefundAppliedHistoryData
// (see audit-events.ts). The fallbacks ($0.00 / Unknown) exist only for
// pre-fix PatientHistory rows that were written before the typed contract.
function readBalancePayment(data: Record<string, unknown>): BalancePaymentAppliedHistoryData {
  return {
    amount: readNumber(data, 'amount'),
    source: readString(data, 'source') ?? 'Unknown',
    fromPaymentStatus: readString(data, 'fromPaymentStatus') ?? '',
    toPaymentStatus: readString(data, 'toPaymentStatus') ?? '',
  }
}

function readBalanceRefund(data: Record<string, unknown>): BalanceRefundAppliedHistoryData {
  return {
    amount: readNumber(data, 'amount'),
    source: readString(data, 'source') ?? 'Unknown',
    fromPaymentStatus: readString(data, 'fromPaymentStatus') ?? '',
    toPaymentStatus: readString(data, 'toPaymentStatus') ?? '',
  }
}
